import React, { useCallback, useEffect, useState } from 'react'
import { RootState } from '../../redux/store'
import PointBar from '../../components/userAdmin/PointBar'
import { jsPDF as JsPDF } from 'jspdf'
import { useAuth } from '../../hooks/useAuth'
import { useSurveyGuard } from '../../hooks/useSurveyGuard'
import { TextElement } from './completed.types'
import { toast } from 'react-toastify'
import { logger } from '../../utils'
import { Button, LoadingSpinner, PageTitle, SkipLink } from '../../components/atoms'
import { setInstitution } from '../../redux/slices/institutionSlice'
import { useDispatch, useSelector } from 'react-redux'
import { fetchCurrentAttempt, getAccount, updateUserProfile } from '../../libs/services'
import { useNavigate } from 'react-router-dom'
import downloadIcon from '../../assets/images/buttons/download-icon.png'
import downloadIconClicked from '../../assets/images/buttons/download-icon-clicked.png'
import certBaseImage from '../../assets/images/cert-base.png'
import { setCurrentAttempt } from '../../redux/slices/quizSlice'
import Text from '../../components/atoms/Text/Text'
import StudentMenu from '../../components/organisms/Menu/StudentMenu'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../enums/KeyboardControls'
import { ControlHighlight, KeyboardControl } from '../../components/molecules/KeyboardControls'
import { useBackgroundMusic } from '../../contexts/BackgroundMusicContext'
import { getBgmUrl } from '../../utils/audio'
import { BgmTrack } from '../../enums/enums'
import { Attempt, Institution, UserProfile } from '../../models/global.types'
import { hasUrlFeatureFlagTrain } from '../../components/molecules/URLFeatureFlag/URLFeatureFlag'
import { waitUntilNotNull } from '../../utils/promise'

export const idCompletedPageMainContent = 'completed-page-main-content'

export enum FocusableElement {
  ButtonDownload = 'button-download',
  ButtonDone = 'button-done',
}

const PdfTextDataYAxis = 400
const PdfTextFontSize = 28
const CyanTuple: [number, number, number] = [15, 52, 67]

const textCertificateSharingUniversity = 'YOUR CERTIFICATE MAY BE SUBMITTED TO YOUR INSTITUTION AND/OR SHARED PUBLICLY AS PROOF OF COURSE COMPLETION.'
const textCertificateSharingTrain = 'YOUR CERTIFICATE MAY BE SHARED PUBLICLY AS PROOF OF SUCCESSFUL TRAINING COMPLETION.'

const urlCertificateUniversity = '/assets/images/Completion_Cert_BLANK.jpg'
const urlCertificateTrain = '/assets/images/Completion_Cert_BLANK_D2C.jpg'

let pdfDataUrl: string | null = null

const Completed: React.FC = () => {
  const [dateCompleted, setDateCompleted] = useState<string | null>(null)
  const { userProfile, refreshUserProfile } = useAuth()
  const { institution } = useSelector((state: RootState) => state.institution)
  const { currentAttempt } = useSelector((state: RootState) => state.quiz)
  const hasFeatureFlagTrainEnabled = hasUrlFeatureFlagTrain()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Track if certificate was already downloaded when this page loaded
  // Used to determine if we should auto-redirect to postsurvey
  const [certWasDownloadedOnMount] = useState(
    () => userProfile?.user_profile?.certificate_downloaded === true
  )

  // Skip auto-redirect if cert wasn't downloaded when we loaded the page
  // (user is downloading for first time and should see the Done button)
  const { pendingSurvey, loading: surveyLoading } = useSurveyGuard({
    skip: !certWasDownloadedOnMount
  })

  // If cert was already downloaded when we arrived and postsurvey is pending, redirect
  useEffect(() => {
    if (!surveyLoading && pendingSurvey === 'postsurvey' && certWasDownloadedOnMount) {
      navigate('/postsurvey', { replace: true })
    }
  }, [surveyLoading, pendingSurvey, certWasDownloadedOnMount, navigate])

  // DMVP-1010: In new flow, survey was completed before cert — go straight to dashboard.
  // Uses window.location.replace instead of navigate to force a full page reload,
  // avoiding a React state race where ProtectedRoute reads stale userProfile.
  const onDone = useCallback(async (): Promise<void> => {
    await refreshUserProfile()
    window.location.replace('/user-dashboard')
  }, [refreshUserProfile])

  const bgMusic = useBackgroundMusic()
  const bgmTrack = getBgmUrl(BgmTrack.Completed)

  let textCertificateSharing = textCertificateSharingUniversity
  if (hasFeatureFlagTrainEnabled) {
    textCertificateSharing = textCertificateSharingTrain
  }

  const getCurrentAttempt = useCallback(async (): Promise<void> => {
    const res = await fetchCurrentAttempt()
    const currentAttempt = res?.data?.data?.attempt
    dispatch(setCurrentAttempt(currentAttempt))
  }, [dispatch])

  // Manage BG music
  useEffect(() => {
    bgMusic.play(bgmTrack)

    return () => {
      bgMusic.pause()
    }
  }, [bgMusic, bgmTrack])

  useEffect(() => {
    if (currentAttempt != null) {
      if (currentAttempt.referenceNumber !== null && currentAttempt?.referenceNumber.length > 0) {
        const fetchInstitution = async (): Promise<void> => {
          if (institution === null) {
            const res = await getAccount()
            dispatch(setInstitution(res.data.account))
          }
        }

        const formattedDate = new Date(currentAttempt.endedAt).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })

        setDateCompleted(formattedDate)

        void fetchInstitution()
      } else { // The user hasn't completed the modules - send them back to dashboard:
        navigate('/user-dashboard')
      }
    } else {
      void getCurrentAttempt()
    }
  }, [currentAttempt, dispatch, getCurrentAttempt, institution, navigate, userProfile])

  useEffect(() => {
    generateCertificatePdf(userProfile, institution, currentAttempt)

    return () => {
      cleanupCertificatePdfUrl()
    }
  }, [currentAttempt, institution, userProfile])

  const certTextClassName = 'text-xs'

  if (dateCompleted !== null && userProfile !== null && userProfile !== undefined) {
    return (
      <div
        className='font-primary bg-gradient-radial min-h-screen flex flex-col justify-between pt-2'
        style={{ // Have to use style directly for tailwind radial gradient:
          backgroundImage: 'radial-gradient(circle, #174E66, #090909)'
        }}
      >
        <SkipLink targetId={idCompletedPageMainContent} />
        <PageTitle title='Certificate of Completion' />
        <header className='pt-2'>
          <StudentMenu />
        </header>
        <main id={idCompletedPageMainContent} tabIndex={-1}>
        <PointBar isCompleted totalExperiencePoints={100} absolutePositioning={false} />
        <div className='px-[100px] text-center'>
          <Text bold shadow size='XL' text='MISSION COMPLETE, CONGRATULATIONS!' />
        </div>
        <div className='flex flex-col justify-center items-center w-full h-[50vh] min-h-96'>
          <div className='relative w-full h-full flex justify-center items-center max-w-3xl'>
            <img
              src={certBaseImage}
              alt='Certificate of completion'
              className='max-w-full max-h-full w-auto h-auto min-w-0 min-h-0 object-scale-down'
            />
            <div className='absolute text-center w-[85%]'>
              <p className='font-bold text-4xl sentry-mask'>
                {`${userProfile?.first_name.toUpperCase()} ${userProfile.last_name.toUpperCase()}`}
              </p>
              {!hasFeatureFlagTrainEnabled && (
                <span>
                  <p className={`${certTextClassName} font-bold pt-12`}>
                    HAS SUCCESSFULLY COMPLETED THE
                  </p>
                  <p className={`${certTextClassName} font-bold`}>
                    OVERDOSE PREVENTION AND RESPONSE COURSE
                  </p>
                  <p className={certTextClassName}>
                    AN ONLINE, NON-CREDIT COURSE AUTHORIZED BY
                  </p>
                  <p className={certTextClassName}>
                    {institution?.name.toUpperCase()}
                  </p>
                  <p className={certTextClassName}>
                    AND OFFERED THROUGH <span className='font-bold'>DOPAGE</span>, BY DEMONSTRATING
                  </p>
                  <p className={certTextClassName}>
                    PROFICIENCY IN OVERDOSE EDUCATION AND PREVENTION.
                  </p>
                </span>
              )}
              {hasFeatureFlagTrainEnabled && (
                <div className='mt-[3em]'>
                  <p className={certTextClassName}>
                    HAS SUCCESSFULLY COMPLETED THE REQUIREMENTS FOR
                  </p>
                  <p className={`${certTextClassName} font-bold underline`}>
                    OVERDOSE PREVENTION AND RESPONSE TRAINING
                  </p>
                  <p className={certTextClassName}>
                    AN ONLINE, NON-CREDIT COURSE OFFERED THROUGH <span className='font-bold'>DOPAGE,</span>
                  </p>
                  <p className={certTextClassName}>
                    BY DEMONSTRATING PROFICIENCY IN COMPREHENSIVE
                  </p>
                  <p className={certTextClassName}>
                    OVERDOSE PREVENTION, RECOGNITION, AND RESPONSE SKILLS,
                  </p>
                  <p className={certTextClassName}>
                    INCLUDING THE USE OF NASAL NALOXONE.
                  </p>
                </div>
              )}
              <div className='flex justify-center'>
                <div className='flex flex-col'>
                  <p className='text-base pr-8 pt-4 underline font-bold'>
                    {dateCompleted.toUpperCase()}
                  </p>
                  <p className='text-sm pr-8'>
                    DATE COMPLETED
                  </p>
                </div>
                <div className='flex flex-col '>
                  <p className='text-base pl-8 pt-4 underline font-bold'>
                    {currentAttempt?.referenceNumber}
                  </p>
                  <p className='text-sm pl-8'>
                    REFERENCE NUMBER
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='flex flex-col items-center justify-center pb-4'>
          <div className='text-center text-dopage-primary'>
            <Text className='text-dopage-lightBlue' bold text={textCertificateSharing} />
            <Text className='text-dopage-lightBlue' bold text='PLEASE DOWNLOAD AND RETAIN THIS CERTIFICATE FOR YOUR RECORDS.' type='paragraph' />
          </div>
          <DownloadButton onDone={onDone} />
        </div>
        </main>
      </div>
    )
  } else {
    return (
      <div
        className='bg-gradient-radial min-h-screen flex flex-col justify-center items-center'
        style={{ // Have to use style directly for tailwind radial gradient:
          backgroundImage: 'radial-gradient(circle, #174E66, #090909)'
        }}
      ><LoadingSpinner className='w-[25vmin] min-w-[80px] max-w-[240px]' />
      </div>
    )
  }
}

interface DownloadButtonProps {
  onDone: () => Promise<void>
}

const DownloadButton: React.FC<DownloadButtonProps> = ({ onDone }) => {
  const [clicked, setClicked] = useState<boolean>(false)
  const keyboardControls = useKeyboardControls()
  const { userProfile, setUserProfile } = useAuth()

  const onDownloadButtonClick = async (): Promise<void> => {
    setClicked(true)

    // Update certificate_downloaded in backend
    try {
      await updateUserProfile({ certificate_downloaded: true })
      // Update local state
      if (userProfile !== null) {
        setUserProfile({
          ...userProfile,
          user_profile: {
            ...userProfile.user_profile,
            certificate_downloaded: true
          }
        })
      }
    } catch (err) {
      console.error('Failed to update certificate_downloaded:', err)
    }

    // Download PDF
    if (pdfDataUrl !== null) {
      const link = document.createElement('a')
      link.href = pdfDataUrl
      link.download = 'certificate.pdf'
      link.click()
    }
  }

  const onDoneButtonClick = (): void => {
    void onDone()
  }

  if (clicked) {
    return (
      <div className='flex flex-row gap-x-8 mt-4'>
        <ControlHighlight
          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonDownload)}
          highlightEffect={HighlightEffect.ButtonHexOutline}
          data-testid={`highlight-${FocusableElement.ButtonDownload}`}
        >
          <div className='diamond-btn bg-white w-[260px] h-[80px] flex justify-center items-center'>
            <KeyboardControl>
              <Button
                id={FocusableElement.ButtonDownload}
                data-testid={FocusableElement.ButtonDownload}
                bgColor='bg-black'
                className='w-[256px] h-[76px] diamond-btn font-bold'
                type='submit'
                onClick={onDownloadButtonClick}
              >
                <div className='flex flex-row justify-between px-[22px]'>
                  <span className='text-white text-[26px] text-neutral-090'>DOWNLOAD</span>
                  <img src={downloadIconClicked} alt='' aria-hidden='true' className='w-[34.64px]' />
                </div>
              </Button>
            </KeyboardControl>
          </div>
        </ControlHighlight>
        <ControlHighlight
          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonDone)}
          highlightEffect={HighlightEffect.ButtonHexOutline}
          data-testid={`highlight-${FocusableElement.ButtonDone}`}
        >
          <KeyboardControl>
            <Button
              id={FocusableElement.ButtonDone}
              data-testid={FocusableElement.ButtonDone}
              className='w-[260px] h-[80px] diamond-btn font-bold'
              type='submit'
              onClick={onDoneButtonClick}
            >
              <div className='flex flex-row justify-center px-[22px]'>
                <span className='text-black text-[26px] text-neutral-090'>DONE</span>
              </div>
            </Button>
          </KeyboardControl>
        </ControlHighlight>
      </div>
    )
  }

  return (
    <ControlHighlight
      active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonDownload)}
      highlightEffect={HighlightEffect.ButtonHexOutline}
      data-testid={`highlight-${FocusableElement.ButtonDownload}`}
      className='mt-4'
    >
      <KeyboardControl>
        <Button
          id={FocusableElement.ButtonDownload}
          data-testid={FocusableElement.ButtonDownload}
          className='w-[260px] h-[80px] diamond-btn font-bold'
          type='submit'
          onClick={onDownloadButtonClick}
        >
          <div className='flex flex-row justify-between items-center px-[22px]'>
            <Text text='DOWNLOAD' />
            <img src={downloadIcon} alt='' aria-hidden='true' className='w-[34.64px]' />
          </div>
        </Button>
      </KeyboardControl>
    </ControlHighlight>
  )
}

async function convertImageToBase64 (file: Blob, callback: (base64: string) => void): Promise<void> {
  const reader = new FileReader()
  reader.onloadend = () => {
    if (reader.result !== null && reader.result !== undefined) {
      const result = reader.result as string
      callback(result)
    } else {
      logger.error('Error: FileReader result is null')
      toast.error('Error occurred when creating the PDF document.')
    }
  }
  reader.onerror = (error) => {
    logger.error(error)
    toast.error('Error occurred when reading the PDF document.')
  }
  reader.readAsDataURL(file)
}

function addTextToPDF (doc: JsPDF, textElement: TextElement): void {
  const { text, x, y, fontSize, font, fontStyle, color = [0, 0, 0], options } = textElement
  doc.setFontSize(fontSize)
  if (font !== undefined && fontStyle !== undefined) {
    doc.setFont(font, fontStyle)
  }
  if (color !== null) {
    doc.setTextColor(...color)
  }
  doc.text(text, x, y, options)
}

/// Returns the PDF data URL.
async function generateCertificatePdf (
  userProfile: UserProfile | null,
  institution: Institution | null,
  currentAttempt: Attempt | null
): Promise<void> {
  cleanupCertificatePdfUrl()
  const hasFeatureFlagTrainEnabled = hasUrlFeatureFlagTrain()

  if (userProfile != null && institution !== null && typeof currentAttempt?.referenceNumber === 'string') {
    let imageBase64: string | null = null
    try {
      let certUrl = urlCertificateUniversity
      if (hasFeatureFlagTrainEnabled) {
        certUrl = urlCertificateTrain
      }

      const response = await fetch(certUrl)
      const blob = await response.blob()
      await convertImageToBase64(blob, (base64Value: string) => {
        imageBase64 = base64Value
      })
    } catch (err: any) {
      logger.error(err)
    }

    await waitUntilNotNull(() => imageBase64)

    const doc = new JsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [612, 792] // Letterbox format
    })

    // For correct font sizing, see: https://raw.githack.com/MrRio/jsPDF/master/docs/module-split_text_to_size.html#~getStringUnitWidth
    const pdfPageWidth = doc.internal.pageSize.getWidth()
    const userName = `${userProfile.first_name} ${userProfile.last_name}`
    const endedAtDate = new Date(currentAttempt.endedAt)
    const formattedDate = endedAtDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })

    const textElements: TextElement[] = [
      {
        text: userName.toUpperCase(), // Overlay effect - stack two text elements w/ user name.
        fontSize: 48,
        font: 'Helvetica',
        fontStyle: 'bold',
        color: [153, 243, 207] as [number, number, number],
        y: 192,
        x: (pdfPageWidth / 2),
        options: { align: 'center' }

      },
      {
        text: userName.toUpperCase(),
        fontSize: 48,
        font: 'Helvetica',
        fontStyle: 'bold',
        color: CyanTuple,
        y: 190,
        x: pdfPageWidth / 2,
        options: { align: 'center' }

      },
      {
        text: formattedDate,
        fontSize: PdfTextFontSize,
        color: CyanTuple,
        y: PdfTextDataYAxis,
        x: 190
      },
      {
        text: currentAttempt?.referenceNumber,
        fontSize: PdfTextFontSize,
        color: CyanTuple,
        y: PdfTextDataYAxis,
        x: 505
      }
    ]

    if (!hasFeatureFlagTrainEnabled) {
      textElements.push({
        text: institution.name.toUpperCase(),
        fontSize: 22,
        color: CyanTuple,
        y: 292,
        x: pdfPageWidth / 2,
        options: { align: 'center' }
      })
    }

    if (imageBase64 !== null) {
      const img = new Image()
      img.src = imageBase64 as string

      const onLoadCallback = () => {
        const pdfWidth: number = doc.internal.pageSize.getWidth()
        const pdfHeight = doc.internal.pageSize.getHeight()
        doc.addImage(imageBase64 as string, 'JPG', 0, 0, pdfWidth, pdfHeight)

        textElements.forEach(textElement => addTextToPDF(doc, textElement))

        const pdfUrl = doc.output('blob') // Changed pdfURL to blob output
        pdfDataUrl = window.URL.createObjectURL(pdfUrl)
      }

      img.onload = onLoadCallback

      img.onerror = (err) => {
        logger.error(err)
      }
    }
  }
}

function cleanupCertificatePdfUrl () {
  if (pdfDataUrl !== null && pdfDataUrl !== '') {
    window.URL.revokeObjectURL(pdfDataUrl)
  }
}

export async function downloadCertificate (
  userProfile: UserProfile | null,
  institution: Institution | null,
  currentAttempt: Attempt | null
): Promise<void> {
  if (pdfDataUrl === null) {
    await generateCertificatePdf(userProfile, institution, currentAttempt)
  }

  await waitUntilNotNull(() => pdfDataUrl)

  const link = document.createElement('a')
  link.href = pdfDataUrl as string
  link.download = 'certificate.pdf'
  link.click()
}

export default Completed
